import React from 'react';
import styled from 'styled-components';
import { Divider, Modal } from '@drone-js/core';
import { ReviewCardProps } from 'component/page/detail/Review/Review.selectors';
import ReviewCardVoteCount from 'component/page/detail/Review/ReviewCard/ReviewCardVoteCount';
import ReviewCardReply from 'component/page/detail/Review/ReviewCard/ReviewCardReply';
import ReviewCardSource from 'component/page/detail/Review/ReviewCard/ReviewCardSource';
import ReviewCardGallery from 'component/page/detail/Review/ReviewCard/ReviewCardGallery';
import ReviewCardHeader from 'component/page/detail/Review/ReviewCard/ReviewCardHeader';
import ReviewCardTitle from 'component/page/detail/Review/ReviewCard/ReviewCardTitle';
import TextToggler from 'component/common/TextToggler/TextToggler';
import ReviewModalGallery from 'component/page/detail/Review/ReviewModalGallery/ReviewModalGallery';
import ReviewCardMinimal from 'component/page/detail/Review/ReviewCard/ReviewCardMinimal';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesReviewCardSeenEventActivitiesFieldsV1,
    ActivitiesReviewModalCloseBtnClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesReviewCardSeenEventActivitiesManifestV1,
    ActivitiesReviewModalCloseBtnClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import ReviewTranslation from 'component/page/detail/Review/ReviewTranslation/ReviewTranslation';
import { InView } from 'react-intersection-observer';
import { body } from 'core-framework/window/document';
import Portal from 'component/common/Portal/Portal';

interface OwnProps {
    index: number;
    review: ReviewCardProps;
    isInOverlay?: boolean;
    minimal?: boolean;
}

type Props = OwnProps;

const ReviewModal = styled(Modal)`
    padding-left: 0 !important;
    padding-right: 0 !important;
`;

const ReviewCard: React.FC<Props> = (props) => {
    const [modalState, setModalState] = React.useState({ visible: false, index: 0 });

    if (!props.review) return null;
    const review = props.review;

    const onSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesReviewCardSeenEventActivitiesFieldsV1>(
            ActivitiesReviewCardSeenEventActivitiesManifestV1.ids,
            {
                is_modal: props.isInOverlay ?? false,
            },
            { action_element_index: props.index + 1, action_element_value: review.id.toString() },
        );
    };

    const isModalVisible = (visible: boolean, index: number) => {
        setModalState({ visible, index });
    };

    const onModalClose = (visible: boolean, index: number) => {
        trackElementEvent<ActivitiesReviewModalCloseBtnClickEventActivitiesFieldsV1>(ActivitiesReviewModalCloseBtnClickEventActivitiesManifestV1.ids, {}, {});
        setModalState({ visible, index });
    };

    if (props.minimal) {
        return <ReviewCardMinimal index={props.index} review={props.review} />;
    }

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={1}>
            <div data-testid="activities-review-content" data-elment-index={props.index}>
                <ReviewCardHeader authorName={review.authorName} reviewDate={review.date} reviewSource={review.source} />
                <ReviewCardTitle reviewTitle={review.title} reviewScore={review.score} reviewSource={review.source} />
                <TextToggler text={review.description} defaultExpanded={false} title={review.title} />
                <ReviewCardGallery reviewMedia={review.media} reviewId={review.id} isModalVisible={isModalVisible} />
                {/* z-index sheet 1200 -> sort modal 1201 -> gallery modal 1202 */}
                <Portal container={body}>
                    <ReviewModal data-testid="review-card-modal-gallery" size="m" visible={modalState.visible} onClose={() => onModalClose(false, 0)} zIndex={1201}>
                        <ReviewModalGallery review={review} currentIndex={modalState.index} />
                    </ReviewModal>
                </Portal>
                <ReviewCardVoteCount voteCount={review.voteCount} />
                <ReviewCardReply reviewReplies={review.replies} reviewId={review.id} />
                <ReviewCardSource reviewSource={review.source} index={props.index} reviewId={review.id} />
                <ReviewTranslation index={props.index} review={review} />
                <Divider my="24" />
            </div>
        </InView>
    );
};

ReviewCard.displayName = 'ReviewCard';

export default ReviewCard;
